import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader } from '@progress/kendo-react-indicators';
import { useAdminAuth } from '../lib/adminAuth';

interface Props {
  children: ReactNode;
}

/**
 * Route guard. While /api/admin/me is in flight render a loader; once it
 * settles redirect to /login (anonymous) or render the children
 * (authenticated). Mirrors RequireAuth from the customer app.
 *
 * If the admin is authenticated but has not enrolled MFA, redirects to
 * /mfa-enroll — unless the session is already MFA-satisfied, which for an
 * unenrolled admin means a local developer sign-in with "Skip MFA" (AAI-115);
 * forcing enrolment there would strand a session the server already trusts.
 * The backend cookie's "mfa-ok=false" claim is enforced server-side too —
 * these client-side redirects are UX only.
 */
export function RequireAdminAuth({ children }: Props) {
  const { state } = useAdminAuth();
  const location = useLocation();

  if (state.status === 'unknown') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <Loader size="large" type="converging-spinner" />
      </div>
    );
  }
  if (state.status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (!state.admin.mfaEnrolled && !state.admin.mfaSatisfied && location.pathname !== '/mfa-enroll') {
    return <Navigate to="/mfa-enroll" replace />;
  }
  return <>{children}</>;
}
